// AuthContext.js
// Real Firebase Auth implementation.
// Login flow matches iOS exactly:
//   1. Look up username in usernames/{USERNAME} → get email
//   2. Sign in with email + password via Firebase Auth
//   3. Load profile from users/{uid}/appData/profile
//
// Sign up flow:
//   1. Check username not already taken in usernames collection
//   2. Create Firebase Auth account
//   3. Write usernames/{USERNAME} → { uid, email, createdAt }
//   4. Write users/{uid} top-level doc + appData/profile

import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // { uid, username, email } | null
  const [isGuest, setIsGuest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isRestoring, setIsRestoring] = useState(true);

  // Free Play: when ON, a logged-in user plays unlimited solo Drafts/Streaks
  // that never submit to the leaderboard and never count as the day's official
  // draft — the same no-stakes path guests get — while staying logged in for
  // everything else (Pick Up, Knockout, profile). Persisted across sessions
  // until manually toggled off. Only ever consulted for logged-in users.
  const [freePlay, setFreePlay] = useState(
    () => localStorage.getItem("rosterable_free_play") === "true"
  );

  // ─── Restore session via Firebase Auth state ──────────────────────────────
  // onAuthStateChanged fires immediately with the cached user on mount —
  // this replaces the localStorage approach with real Firebase session restore.

  useEffect(() => {
    // Check for guest session in localStorage
    const guestStored = localStorage.getItem("rosterable_guest");

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Try top-level users/{uid} doc first (has username field)
          const userSnap = await getDoc(doc(db, "users", firebaseUser.uid));
          let username = userSnap.exists() ? userSnap.data().username : null;

          // Fallback: try appData/profile
          if (!username) {
            const profileSnap = await getDoc(
              doc(db, "users", firebaseUser.uid, "appData", "profile")
            );
            username = profileSnap.exists() ? profileSnap.data().username : null;
          }

          // Final fallback: derive from email (remove domain)
          if (!username) {
            username = firebaseUser.email?.split("@")[0]?.toUpperCase() || firebaseUser.email;
          }

          setUser({ uid: firebaseUser.uid, username, email: firebaseUser.email });
        } catch {
          setUser({ uid: firebaseUser.uid, username: firebaseUser.email, email: firebaseUser.email });
        }
        setIsGuest(false);
        localStorage.removeItem("rosterable_guest");
      } else if (guestStored === "true") {
        // No Firebase user but guest flag set
        setUser(null);
        setIsGuest(true);
      } else {
        setUser(null);
        setIsGuest(false);
      }
      setIsRestoring(false);
    });

    return unsubscribe;
  }, []);

  const isLoggedIn = !!user || isGuest;

  // ─── Login ────────────────────────────────────────────────────────────────
  // Mirrors UserManager.swift signIn(username:password:) exactly.
  // Step 1: look up email from usernames/{USERNAME}
  // Step 2: signInWithEmailAndPassword

  async function login(username, password) {
    setIsLoading(true);
    setAuthError(null);
    try {
      // Step 1: resolve username → email
      const usernameDoc = await getDoc(
        doc(db, "usernames", username.toUpperCase().trim())
      );
      if (!usernameDoc.exists()) {
        throw new Error("Incorrect username or password.");
      }
      const email = usernameDoc.data().email;

      // Step 2: sign in
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged will fire and set user state automatically
    } catch (err) {
      const message = friendlyAuthError(err);
      setAuthError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Sign Up ──────────────────────────────────────────────────────────────
  // Mirrors UserManager.swift signUp(username:email:password:).

  async function signUp(username, email, password) {
    setIsLoading(true);
    setAuthError(null);
    try {
      const upperUsername = username.toUpperCase().trim();

      // Step 1: check username not taken
      const usernameSnap = await getDoc(doc(db, "usernames", upperUsername));
      if (usernameSnap.exists()) {
        throw new Error("Username is already taken. Please choose another.");
      }

      // Step 2: create Firebase Auth account
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const uid = credential.user.uid;

      // Step 3: write usernames/{USERNAME} — matches iOS UserManager exactly
      await setDoc(doc(db, "usernames", upperUsername), {
        uid,
        email: email.trim(),
        createdAt: serverTimestamp(),
      });

      // Step 4: write top-level user doc + appData/profile
      await setDoc(doc(db, "users", uid), {
        username: upperUsername,
        email: email.trim(),
        createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, "users", uid, "appData", "profile"), {
        username: upperUsername,
        email: email.trim(),
        createdAt: serverTimestamp(),
        level: 1,
        totalXP: 0,
      });

      // onAuthStateChanged fires and sets user state automatically
    } catch (err) {
      const message = friendlyAuthError(err);
      setAuthError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Continue as Guest ────────────────────────────────────────────────────

  function continueAsGuest() {
    setUser(null);
    setIsGuest(true);
    setAuthError(null);
    localStorage.setItem("rosterable_guest", "true");
  }

  // ─── Logout ───────────────────────────────────────────────────────────────

  async function logout() {
    setAuthError(null);
    localStorage.removeItem("rosterable_guest");
    if (auth.currentUser) {
      await signOut(auth);
    }
    setUser(null);
    setIsGuest(false);
  }

  // ─── Password Reset ───────────────────────────────────────────────────────
  // mode "username": look up email from usernames collection first.
  // mode "email": send directly to the provided email.
  // Always shows a generic success message — never confirms account existence.

  async function sendPasswordReset(mode, value) {
    try {
      let email = value.trim();

      if (mode === "username") {
        const snap = await getDoc(
          doc(db, "usernames", value.toUpperCase().trim())
        );
        // If username not found we still show success — never confirm existence
        if (snap.exists()) {
          email = snap.data().email;
          await sendPasswordResetEmail(auth, email);
        }
      } else {
        await sendPasswordResetEmail(auth, email);
      }
    } catch {
      // Swallow errors — always show success message
    }
    return "✅ If an account exists, a reset link has been sent.";
  }

  // ─── Free Play toggle ─────────────────────────────────────────────────────

  function toggleFreePlay() {
    setFreePlay(prev => {
      const next = !prev;
      try { localStorage.setItem("rosterable_free_play", next ? "true" : "false"); } catch {}
      return next;
    });
  }

  return (
    <AuthContext.Provider value={{
      user,
      isGuest,
      isLoggedIn,
      isLoading,
      authError,
      isRestoring,
      freePlay,
      toggleFreePlay,
      login,
      signUp,
      continueAsGuest,
      logout,
      sendPasswordReset,
      clearError: () => setAuthError(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ─── Error message helpers ─────────────────────────────────────────────────

function friendlyAuthError(err) {
  const code = err?.code || "";
  if (code === "auth/user-not-found" || code === "auth/wrong-password" || code === "auth/invalid-credential") {
    return "Incorrect username or password.";
  }
  if (code === "auth/email-already-in-use") {
    return "An account with this email already exists.";
  }
  if (code === "auth/weak-password") {
    return "Password must be at least 6 characters.";
  }
  if (code === "auth/invalid-email") {
    return "Please enter a valid email address.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Please try again later.";
  }
  if (code === "auth/network-request-failed") {
    return "Network error. Please check your connection.";
  }
  return err?.message || "Something went wrong. Please try again.";
}
